import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "counter", "reveal", "magnetic", "typewriter", "heroTitle", "heroSub", "heroCta", "orb"]

  connect() {
    this.particles = []
    this.mouse = { x: 0, y: 0 }
    this.scrollY = 0
    this.rafId = null
    this.countersAnimated = false

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const isSmall = window.matchMedia("(max-width: 768px)").matches
    this.reduceMotion = reduceMotion
    this.isSmall = isSmall

    this.initLazyVideos()
    if (!reduceMotion && !isSmall) this.initCanvas()
    this.initScrollReveals()
    if (!reduceMotion) this.initTypewriter()
    if (!reduceMotion) this.initMagneticButtons()
    this.initHeroEntrance()
    if (!reduceMotion) this.initParallaxOrbs()
    if (!reduceMotion && !isSmall) this.initTiltCards()

    this.boundScroll = this.onScroll.bind(this)
    this.boundMouse = this.onMouseMove.bind(this)
    this.boundResize = this.onResize.bind(this)

    window.addEventListener("scroll", this.boundScroll, { passive: true })
    if (!reduceMotion && !isSmall) {
      window.addEventListener("mousemove", this.boundMouse, { passive: true })
    }
    window.addEventListener("resize", this.boundResize)
  }

  disconnect() {
    window.removeEventListener("scroll", this.boundScroll)
    window.removeEventListener("mousemove", this.boundMouse)
    window.removeEventListener("resize", this.boundResize)
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this.videoObserver) this.videoObserver.disconnect()
  }

  // ─── Lazy-load Videos ──────────────────────────────────────
  initLazyVideos() {
    const videos = Array.from(this.element.querySelectorAll("video[data-lazy-video]"))
    if (videos.length === 0) return

    const loadVideo = (video) => {
      if (video.dataset.loaded === "true") return
      video.dataset.loaded = "true"
      video.querySelectorAll("source[data-src]").forEach((source) => {
        source.src = source.dataset.src
      })
      video.load()
      const playPromise = video.play()
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {})
      }
    }

    // Hero videos: load after first idle/paint so they don't block LCP
    const heroVideos = videos.filter((v) => v.dataset.lazyVideo === "hero")
    const viewportVideos = videos.filter((v) => v.dataset.lazyVideo !== "hero")

    const kickHero = () => heroVideos.forEach(loadVideo)
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(kickHero, { timeout: 2000 })
    } else {
      setTimeout(kickHero, 800)
    }

    // Below-the-fold videos: IntersectionObserver
    if (viewportVideos.length === 0) return
    if (!("IntersectionObserver" in window)) {
      viewportVideos.forEach(loadVideo)
      return
    }

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadVideo(entry.target)
            this.videoObserver.unobserve(entry.target)
          }
        })
      },
      { rootMargin: "200px 0px" }
    )
    viewportVideos.forEach((v) => this.videoObserver.observe(v))
  }

  // ─── Particle Canvas ───────────────────────────────────────
  initCanvas() {
    if (!this.hasCanvasTarget) return
    const canvas = this.canvasTarget
    this.ctx = canvas.getContext("2d")
    this.resizeCanvas()

    const count = 40
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.1,
        hue: Math.random() * 60 + 240
      })
    }

    this.animateParticles()
  }

  resizeCanvas() {
    if (!this.hasCanvasTarget) return
    const canvas = this.canvasTarget
    const rect = canvas.parentElement.getBoundingClientRect()
    canvas.width = rect.width * window.devicePixelRatio
    canvas.height = rect.height * window.devicePixelRatio
    canvas.style.width = rect.width + "px"
    canvas.style.height = rect.height + "px"
    this.ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
  }

  animateParticles() {
    if (!this.ctx || !this.hasCanvasTarget) return
    const canvas = this.canvasTarget
    const w = canvas.width / window.devicePixelRatio
    const h = canvas.height / window.devicePixelRatio

    this.ctx.clearRect(0, 0, w, h)

    this.particles.forEach((p, i) => {
      p.x += p.vx
      p.y += p.vy

      // Mouse attraction
      const dx = this.mouse.x - p.x
      const dy = this.mouse.y - p.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < 200) {
        p.vx += dx * 0.00005
        p.vy += dy * 0.00005
      }

      // Wrap around
      if (p.x < 0) p.x = w
      if (p.x > w) p.x = 0
      if (p.y < 0) p.y = h
      if (p.y > h) p.y = 0

      // Draw particle
      this.ctx.beginPath()
      this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
      this.ctx.fillStyle = `hsla(${p.hue}, 80%, 70%, ${p.opacity})`
      this.ctx.fill()

      // Draw connections
      for (let j = i + 1; j < this.particles.length; j++) {
        const p2 = this.particles[j]
        const d = Math.sqrt((p.x - p2.x) ** 2 + (p.y - p2.y) ** 2)
        if (d < 120) {
          this.ctx.beginPath()
          this.ctx.moveTo(p.x, p.y)
          this.ctx.lineTo(p2.x, p2.y)
          this.ctx.strokeStyle = `hsla(260, 70%, 65%, ${0.15 * (1 - d / 120)})`
          this.ctx.lineWidth = 0.5
          this.ctx.stroke()
        }
      }
    })

    this.rafId = requestAnimationFrame(() => this.animateParticles())
  }

  // ─── Scroll Reveal ─────────────────────────────────────────
  initScrollReveals() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.revealDelay || 0
            setTimeout(() => {
              entry.target.classList.add("revealed")
            }, delay)
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.15, rootMargin: "0px 0px -50px 0px" }
    )

    this.revealTargets.forEach((el) => observer.observe(el))
  }

  // ─── Typewriter ────────────────────────────────────────────
  initTypewriter() {
    if (!this.hasTypewriterTarget) return
    const el = this.typewriterTarget
    const words = JSON.parse(el.dataset.words || '["Powered by AI"]')
    let wordIndex = 0
    let charIndex = 0
    let isDeleting = false
    let speed = 80

    const type = () => {
      const current = words[wordIndex]
      if (isDeleting) {
        el.textContent = current.substring(0, charIndex - 1)
        charIndex--
        speed = 40
      } else {
        el.textContent = current.substring(0, charIndex + 1)
        charIndex++
        speed = 80
      }

      if (!isDeleting && charIndex === current.length) {
        speed = 2000
        isDeleting = true
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false
        wordIndex = (wordIndex + 1) % words.length
        speed = 300
      }

      setTimeout(type, speed)
    }

    setTimeout(type, 1200)
  }

  // ─── Magnetic Buttons ──────────────────────────────────────
  initMagneticButtons() {
    this.magneticTargets.forEach((btn) => {
      btn.addEventListener("mousemove", (e) => {
        const rect = btn.getBoundingClientRect()
        const x = e.clientX - rect.left - rect.width / 2
        const y = e.clientY - rect.top - rect.height / 2
        btn.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`
      })

      btn.addEventListener("mouseleave", () => {
        btn.style.transform = "translate(0, 0)"
        btn.style.transition = "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
      })

      btn.addEventListener("mouseenter", () => {
        btn.style.transition = "transform 0.15s ease-out"
      })
    })
  }

  // ─── Hero Entrance ─────────────────────────────────────────
  initHeroEntrance() {
    const allHeroEls = [
      ...this.heroTitleTargets,
      ...this.heroSubTargets,
      ...this.heroCtaTargets
    ]

    allHeroEls.forEach((el, i) => {
      setTimeout(() => {
        el.classList.add("hero-entered")
      }, 200 + i * 200)
    })
  }

  // ─── Parallax Orbs ────────────────────────────────────────
  initParallaxOrbs() {
    this.orbElements = this.orbTargets
  }

  // ─── 3D Tilt Cards ────────────────────────────────────────
  initTiltCards() {
    document.querySelectorAll("[data-tilt]").forEach((card) => {
      card.addEventListener("mousemove", (e) => {
        const rect = card.getBoundingClientRect()
        const x = (e.clientX - rect.left) / rect.width
        const y = (e.clientY - rect.top) / rect.height
        const rotateX = (y - 0.5) * -10
        const rotateY = (x - 0.5) * 10
        card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`
      })

      card.addEventListener("mouseleave", () => {
        card.style.transform = "perspective(800px) rotateX(0) rotateY(0) scale3d(1, 1, 1)"
        card.style.transition = "transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
      })

      card.addEventListener("mouseenter", () => {
        card.style.transition = "transform 0.15s ease-out"
      })
    })
  }

  // ─── Event Handlers ────────────────────────────────────────
  onScroll() {
    this.scrollY = window.scrollY

    // Parallax orbs
    this.orbElements?.forEach((orb) => {
      const speed = parseFloat(orb.dataset.speed || 0.3)
      orb.style.transform = `translateY(${this.scrollY * speed}px)`
    })

    // Counters
    if (!this.countersAnimated) {
      this.counterTargets.forEach((el) => {
        const rect = el.getBoundingClientRect()
        if (rect.top < window.innerHeight * 0.85) {
          this.animateCounter(el)
        }
      })
    }
  }

  onMouseMove(e) {
    if (this.hasCanvasTarget) {
      const rect = this.canvasTarget.getBoundingClientRect()
      this.mouse.x = e.clientX - rect.left
      this.mouse.y = e.clientY - rect.top
    }
  }

  onResize() {
    this.resizeCanvas()
  }

  // ─── Counter Animation ─────────────────────────────────────
  animateCounter(el) {
    if (el.dataset.animated) return
    el.dataset.animated = "true"

    const target = parseInt(el.dataset.target)
    const suffix = el.dataset.suffix || ""
    const duration = 2000
    const start = performance.now()

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = Math.floor(eased * target)
      el.textContent = current.toLocaleString() + suffix
      if (progress < 1) requestAnimationFrame(step)
    }

    requestAnimationFrame(step)
  }
}
